import React from 'react';

type Props = {
  values: number[];
  width?: number;
  height?: number;
  strokeColor?: string;
  fillColor?: string;
};

/**
 * Tiny inline-SVG sparkline. No library, no axes, no tooltips.
 * Renders a line + filled area for the given values, scaled to fit the box.
 * If all values are zero (or empty), renders a flat baseline.
 */
export const Sparkline: React.FC<Props> = ({
  values,
  width = 120,
  height = 28,
  strokeColor = '#01bcd4',
  fillColor = 'rgba(1, 188, 212, 0.15)',
}) => {
  if (!values || values.length === 0) {
    return <svg width={width} height={height} />;
  }

  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width.toFixed(1)},${height} L 0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <path d={areaPath} fill={fillColor} stroke="none" />
      <path d={linePath} fill="none" stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};
